import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/utils';
import { usePointerActivation } from '@/hooks';

interface ResourcesCoachmarkProps {
	open: boolean;
	anchorRect: DOMRect | null;
	title: string;
	body: string;
	onDismiss: () => void;
	onAnchorClick: () => void;
}

const TOOLTIP_WIDTH = 300;
const TOOLTIP_GAP = 12;
const TOOLTIP_ESTIMATED_HEIGHT = 132;
const VIEWPORT_MARGIN = 16;
const ARROW_SIZE = 12;
const COACHMARK_Z_INDEX = 1_000_001;
const SPOTLIGHT_PADDING = 8;
const SPOTLIGHT_RADIUS = 12;

const buildRoundedHoleMask = (width: number, height: number): string => {
	const svg =
		`<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">` +
		`<rect width="${width}" height="${height}" rx="${SPOTLIGHT_RADIUS}" fill="#fff"/></svg>`;

	return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

export function ResourcesCoachmark({
	open,
	anchorRect,
	title,
	body,
	onDismiss,
	onAnchorClick,
}: ResourcesCoachmarkProps) {
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	useEffect(() => {
		if (!open) return;

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onDismiss();
		};

		document.addEventListener('keydown', handleKeyDown);

		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [open, onDismiss]);

	const anchorPointerProps = usePointerActivation(onAnchorClick);
	const gotItPointerProps = usePointerActivation(onDismiss);

	if (!mounted || !open || !anchorRect) return null;

	const spotlightTop = Math.round(anchorRect.top - SPOTLIGHT_PADDING);
	const spotlightLeft = Math.round(anchorRect.left - SPOTLIGHT_PADDING);
	const spotlightWidth = Math.round(anchorRect.width + SPOTLIGHT_PADDING * 2);
	const spotlightHeight = Math.round(anchorRect.height + SPOTLIGHT_PADDING * 2);
	const spotlightBottom = spotlightTop + spotlightHeight;

	const anchorCenterX = anchorRect.left + anchorRect.width / 2;

	let tooltipTop = spotlightBottom + TOOLTIP_GAP;
	let arrowOnTop = true;

	if (tooltipTop + TOOLTIP_ESTIMATED_HEIGHT > window.innerHeight - VIEWPORT_MARGIN) {
		tooltipTop = spotlightTop - TOOLTIP_ESTIMATED_HEIGHT - TOOLTIP_GAP;
		arrowOnTop = false;
	}

	let tooltipLeft = spotlightLeft + spotlightWidth - TOOLTIP_WIDTH;
	tooltipLeft = Math.max(
		VIEWPORT_MARGIN,
		Math.min(tooltipLeft, window.innerWidth - TOOLTIP_WIDTH - VIEWPORT_MARGIN)
	);

	const arrowLeft = Math.min(
		Math.max(anchorCenterX - tooltipLeft - ARROW_SIZE / 2, VIEWPORT_MARGIN),
		TOOLTIP_WIDTH - VIEWPORT_MARGIN - ARROW_SIZE / 2
	);

	const spotlightBox = {
		top: spotlightTop,
		left: spotlightLeft,
		width: spotlightWidth,
		height: spotlightHeight,
		borderRadius: SPOTLIGHT_RADIUS,
	} as const;

	const holeMask = buildRoundedHoleMask(spotlightWidth, spotlightHeight);

	const scrimMaskStyle = {
		WebkitMaskImage: `linear-gradient(#fff 0 0), ${holeMask}`,
		maskImage: `linear-gradient(#fff 0 0), ${holeMask}`,
		WebkitMaskSize: `100% 100%, ${spotlightWidth}px ${spotlightHeight}px`,
		maskSize: `100% 100%, ${spotlightWidth}px ${spotlightHeight}px`,
		WebkitMaskPosition: `0 0, ${spotlightLeft}px ${spotlightTop}px`,
		maskPosition: `0 0, ${spotlightLeft}px ${spotlightTop}px`,
		WebkitMaskRepeat: 'no-repeat',
		maskRepeat: 'no-repeat',
		WebkitMaskComposite: 'xor',
		maskComposite: 'exclude',
	} as const;

	return createPortal(
		<div className="fixed inset-0" style={{ zIndex: COACHMARK_Z_INDEX }}>
			<div className="absolute inset-0" onPointerDown={onDismiss} />

			<div
				className="pointer-events-none fixed inset-0 bg-[rgba(20,24,33,0.32)] backdrop-blur-xs"
				style={scrimMaskStyle}
			/>

			<div
				className="pointer-events-none absolute bg-[rgba(255,255,255,0.32)]"
				style={spotlightBox}
			/>

			<button
				type="button"
				aria-label="Open Grand Slam Challenge guide"
				{...anchorPointerProps}
				className="absolute"
				style={{ ...spotlightBox, zIndex: COACHMARK_Z_INDEX + 1 }}
			/>

			<div
				className="absolute rounded-[12px] bg-blue-custom-2 p-3 font-['DM_Sans'] text-white shadow-[0_12px_28px_rgba(38,103,255,0.25)]"
				style={{
					top: tooltipTop,
					left: tooltipLeft,
					width: TOOLTIP_WIDTH,
					zIndex: COACHMARK_Z_INDEX + 1,
				}}
			>
				<div
					className={cn(
						'absolute size-3 rotate-45 bg-blue-custom-2',
						arrowOnTop ? 'top-[-6px]' : 'bottom-[-6px]'
					)}
					style={{ left: arrowLeft }}
				/>
				{title ? <p className="text-sm font-bold leading-4 tracking-[-0.28px]">{title}</p> : null}
				{body ? (
					<p
						className={cn(
							'text-xs font-medium leading-[18px] tracking-[-0.24px] text-white/90',
							title && 'mt-1'
						)}
					>
						{body}
					</p>
				) : null}
				<button
					type="button"
					{...gotItPointerProps}
					className="mt-3 flex w-full items-center justify-end gap-1 text-xs font-semibold leading-[14px] tracking-[-0.24px] text-white"
				>
					Got it
					<ArrowRight className="size-3" strokeWidth={2} />
				</button>
			</div>
		</div>,
		document.body
	);
}
